import type {
  ActiveRoomSnapshot,
  ApiResult,
  RoomMessage,
  RoomSummary,
  UserDashboardSummary,
} from "@topzle/types";

export interface TopzleApiClientOptions {
  baseUrl: string;
  getAccessToken?: () => Promise<string | null>;
  fetcher?: typeof fetch;
  createIdempotencyKey?: () => string;
  requestTimeoutMs?: number;
}
export interface RealtimeSessionDescription {
  sdp: string;
  type: "offer" | "answer";
}
export interface RealtimeTrackRequest {
  location: "local" | "remote";
  trackName: string;
  mid?: string;
  sessionId?: string;
  kind?: "audio" | "video";
  quality?: "low" | "high";
  simulcast?: {
    preferredRid: string;
    priorityOrdering?: "none" | "asciibetical";
    ridNotAvailable?: "none" | "asciibetical";
  };
}
export interface RealtimeTracksResponse {
  errorCode?: string;
  errorDescription?: string;
  requiresImmediateRenegotiation?: boolean;
  sessionDescription?: RealtimeSessionDescription;
  tracks?: Array<{
    trackName: string;
    mid?: string;
    sessionId?: string;
  }>;
}
export interface MediaLease {
  allowed: boolean;
  mode: "audio" | "video";
  expiresAt: number;
  userCreditsRemaining: number;
  dailyCreditsRemaining: number;
  dailyCreditsLimit: number;
  userCreditsLimit: number;
  limitReason: "user" | "application" | null;
  resetsAt: number;
}
export interface MediaAccessDecision {
  allowed: boolean;
  provider: "p2p" | "cloudflare_realtime";
  reason: "calls_paused" | "anonymous_calls_disabled" | "phone_verification_required" | null;
  action: "sign_in" | "verify_phone" | null;
  message: string | null;
}
export interface PhoneAuthSession {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number;
  token_type: string;
  user: {
    id: string;
    phone?: string;
    email?: string;
    app_metadata: Record<string, unknown>;
    user_metadata: Record<string, unknown>;
  };
}
export type AuthAudience = "player" | "admin";
export interface AuthRequestResult {
  sent: boolean;
  channel: "email" | "phone";
  methods: Array<"magic_link" | "otp">;
  expiresInSeconds: number;
  resendAfterSeconds: number;
  destination: string;
}
export interface VisitorContext {
  countryCode: string | null;
  countryName: string | null;
  city: string | null;
  region: string | null;
  timeZone: string | null;
  locale: string;
  currencyCode: string | null;
  currencySymbol: string | null;
  callingCode: string | null;
  countryFlag: string | null;
  source:
    | "cloudflare-edge"
    | "header-fallback"
    | "manual"
    | "unknown";
}
export interface LocaleSettings {
  context: {
    country_code: string | null;
    currency_code: string | null;
    city: string | null;
    region: string | null;
    timezone: string | null;
    source: string;
  };
  showCountryFlag: boolean;
}
export class TopzleApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "TopzleApiError";
  }
}
export class TopzleApiClient {
  private readonly fetcher: typeof fetch;
  private readonly createIdempotencyKey: () => string;
  constructor(private readonly options: TopzleApiClientOptions) {
    const fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
    // Keep fetch detached from the client instance. Native browser fetch throws
    // "Illegal invocation" when it is called with TopzleApiClient as `this`.
    this.fetcher = (input, init) => fetcher(input, init);
    this.createIdempotencyKey =
      options.createIdempotencyKey ??
      (() => {
        const nativeUuid = globalThis.crypto?.randomUUID?.();
        if (nativeUuid) return nativeUuid;
        // Idempotency keys are request correlation values, not credentials. Native
        // clients inject a cryptographically random UUID; this fallback keeps the
        // shared client usable in runtimes without Web Crypto.
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random()
          .toString(36)
          .slice(2)}`;
      });
  }
  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.options.getAccessToken?.();
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    // Send only browser/app locale preferences. The Worker uses these for
    // formatting and profile defaults; it never performs IP geolocation.
    if (typeof navigator !== "undefined") {
      const languages = Array.isArray(navigator.languages)
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : [];
      if (languages.length > 0 && !headers.has("Accept-Language"))
        headers.set("Accept-Language", languages.join(","));
    }
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timeZone) headers.set("X-Topzle-Timezone", timeZone);
    } catch {
      // Some embedded runtimes do not expose Intl timezone data.
    }
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (init.method && init.method !== "GET")
      headers.set("Idempotency-Key", this.createIdempotencyKey());
    const controller = new AbortController();
    const timeoutMs = this.options.requestTimeoutMs ?? 30_000;
    const timeout = setTimeout(() => controller.abort("request_timeout"), timeoutMs);
    const abortFromCaller = () => controller.abort(init.signal?.reason);
    init.signal?.addEventListener("abort", abortFromCaller, { once: true });
    let response: Response;
    try {
      // React Native's Android fetch bridge expects a URI string. Passing a WHATWG
      // URL object works in browsers but can leave the native request unresolved.
      const requestUrl = new URL(path, this.options.baseUrl).toString();
      response = await this.fetcher(requestUrl, {
        ...init,
        headers,
        signal: controller.signal,
      });
    } catch (error) {
      if (controller.signal.aborted && !init.signal?.aborted)
        throw new TopzleApiError(
          408,
          "request_timeout",
          "Topzle took too long to respond. Check your connection and try again.",
        );
      throw error;
    } finally {
      clearTimeout(timeout);
      init.signal?.removeEventListener("abort", abortFromCaller);
    }
    const result = (await response.json()) as ApiResult<T>;
    if (!result.ok)
      throw new TopzleApiError(
        response.status,
        result.error.code,
        result.error.message,
        result.error.requestId,
        result.error.details,
      );
    return result.data;
  }
  health() {
    return this.request<{ status: string; version: string }>("/api/health");
  }
  visitorContext() {
    return this.request<VisitorContext>("/api/visitor-context");
  }
  countries() {
    return this.request<Array<{ code: string; name: string; flag: string }>>("/api/countries");
  }
  localeSettings() {
    return this.request<LocaleSettings>("/api/me/locale");
  }
  updateLocaleSettings(input: { countryCode: string; showCountryFlag: boolean }) {
    return this.request<LocaleSettings>("/api/me/locale", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }
  gameCatalog() {
    return this.request<Array<{ id: string; name: string; description: string }>>("/api/games");
  }
  mediaAccess() {
    return this.request<MediaAccessDecision>("/api/media/access");
  }
  dashboard() {
    return this.request<UserDashboardSummary>("/api/me/dashboard");
  }
  removeDashboardRoom(roomId: string) {
    return this.request<{ roomId: string; removed: boolean }>(
      `/api/me/rooms/${encodeURIComponent(roomId)}`,
      { method: "DELETE" },
    );
  }
  createGuest(turnstileToken?: string) {
    return this.request<{
      id: string;
      token: string;
      displayName: string;
      avatar: string;
      countryCode: string | null;
      countryFlag: string | null;
      showCountryFlag: boolean;
    }>("/api/auth/guest", { method: "POST", body: JSON.stringify({ turnstileToken }) });
  }
  requestAuth(input: {
    identifier: string;
    audience: AuthAudience;
    turnstileToken: string;
    returnTo?: string;
  }) {
    return this.request<AuthRequestResult>("/api/auth/request", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  verifyAuthOtp(input: { identifier: string; audience: AuthAudience; code: string }) {
    return this.request<{ session: PhoneAuthSession }>("/api/auth/verify", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  verifyMagicLink(input: { challengeId: string; token: string; audience: AuthAudience }) {
    return this.request<{ session: PhoneAuthSession }>("/api/auth/magic/verify", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  requestPhoneOtp(input: { phone: string; turnstileToken: string }) {
    return this.request<{
      sent: boolean;
      expiresInSeconds: number;
      resendAfterSeconds: number;
      destination: string;
    }>("/api/auth/phone/request", { method: "POST", body: JSON.stringify(input) });
  }
  verifyPhoneOtp(input: { phone: string; code: string }) {
    return this.request<{ session: PhoneAuthSession }>("/api/auth/phone/verify", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  onboardingStatus() {
    return this.request<{
      displayName: string;
      email: string;
      phone: string;
      phoneVerified: boolean;
      dateOfBirthSet: boolean;
      onboardingCompleted: boolean;
      ageBand: "unknown" | "child" | "teen" | "adult";
      parentalConsentStatus:
        | "not_required"
        | "required"
        | "pending"
        | "verified"
        | "rejected"
        | "revoked";
      guardianEmailHint: string | null;
    }>("/api/me/onboarding");
  }
  resendParentalConsent() {
    return this.request<{ sent: true; guardianEmailHint: string }>(
      "/api/me/parental-consent/resend",
      { method: "POST", body: "{}" },
    );
  }
  linkVerifiedPhone(input: { phone: string; code: string }) {
    return this.request<{ linked: true; phone: string }>("/api/me/phone/verify", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  completeOnboarding(input: {
    displayName: string;
    email: string;
    dateOfBirth: string;
    acceptedTerms: true;
    acceptedPrivacy: true;
    guardianEmail?: string;
    parentalConsentAcknowledged?: true;
  }) {
    return this.request<{
      onboardingCompleted: boolean;
      ageBand: "child" | "teen" | "adult";
      parentalConsentRequired: boolean;
      parentalConsentEmailSent: boolean;
      capabilities: {
        socialPlay: boolean;
        freeTextChat: boolean;
        voice: boolean;
        video: boolean;
      };
    }>("/api/me/onboarding", { method: "POST", body: JSON.stringify(input) });
  }
  parentalConsentPreview(token: string) {
    const search = new URLSearchParams({ token });
    return this.request<{
      childName: string;
      guardianEmailHint: string;
      expiresAt: string;
    }>(`/api/parental-consent?${search.toString()}`);
  }
  approveParentalConsent(input: {
    token: string;
    guardianDeclaration: true;
    acceptedTerms: true;
    acceptedPrivacy: true;
  }) {
    return this.request<{ approved: true; childName: string }>("/api/parental-consent", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  sendEmailInvitation(input: { to: string; roomId: string }) {
    return this.request<{ sent: boolean }>("/api/invitations/email", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  createRoom(input: {
    title: string;
    starterGameId?: string;
    visibility?: string;
    joinPolicy?: "open" | "invite" | "approval";
    aiPlayersEnabled?: boolean;
    aiDifficulty?: "easy" | "medium" | "hard";
    aiArrivalBehavior?: "end_ai_game" | "spectate_ai_game";
  }) {
    return this.request<RoomSummary & { joinToken: string }>("/api/rooms", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  publicWaitingRooms(gameId: string, limit = 4) {
    const search = new URLSearchParams({ gameId, limit: String(limit) });
    return this.request<{ rooms: RoomSummary[] }>(`/api/rooms/public/waiting?${search.toString()}`);
  }
  previewRoom(code: string) {
    return this.request<RoomSummary>(`/api/rooms/code/${encodeURIComponent(code)}`);
  }
  joinRoom(roomId: string, input: { displayName: string; avatar: string; role?: string }) {
    return this.request<{
      room: ActiveRoomSnapshot;
      participantId: string;
      websocketUrl: string;
      reconnectToken: string;
      realtimeSignalToken: string;
      mediaToken: string | null;
    }>(`/api/rooms/${roomId}/join`, { method: "POST", body: JSON.stringify(input) });
  }
  startRoomGame(
    roomId: string,
    input: {
      gameId: string;
      settings?: unknown;
      versusAi: boolean;
      aiDifficulty: "easy" | "medium" | "hard";
      aiPolicy?: string;
      challengerId?: string;
    },
  ) {
    return this.request<{ room: ActiveRoomSnapshot; event: RoomMessage }>(
      `/api/rooms/${roomId}/games/start`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  }
  createMediaSession(roomId: string) {
    return this.request<{ sessionId: string }>(
      `/api/rooms/${encodeURIComponent(roomId)}/media/session`,
      { method: "POST", body: "{}" },
    );
  }
  leaseMedia(
    roomId: string,
    input: {
      mode: "audio" | "video";
      consumerId: string;
      credits: number;
      sessionId?: string;
      mids?: string[];
    },
  ) {
    return this.request<MediaLease>(`/api/rooms/${encodeURIComponent(roomId)}/media/lease`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  addMediaTracks(
    roomId: string,
    sessionId: string,
    input: {
      sessionDescription?: RealtimeSessionDescription;
      tracks: RealtimeTrackRequest[];
    },
  ) {
    return this.request<RealtimeTracksResponse>(
      `/api/rooms/${encodeURIComponent(roomId)}/media/${encodeURIComponent(sessionId)}/tracks`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }
  updateMediaTracks(roomId: string, sessionId: string, input: { tracks: RealtimeTrackRequest[] }) {
    return this.request<RealtimeTracksResponse>(
      `/api/rooms/${encodeURIComponent(roomId)}/media/${encodeURIComponent(sessionId)}/tracks/update`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  }
  closeMediaTracks(roomId: string, sessionId: string, mids: string[]) {
    return this.request<RealtimeTracksResponse>(
      `/api/rooms/${encodeURIComponent(roomId)}/media/${encodeURIComponent(sessionId)}/tracks/close`,
      {
        method: "PUT",
        body: JSON.stringify({ tracks: mids.map((mid) => ({ mid })), force: true }),
      },
    );
  }
  renegotiateMedia(
    roomId: string,
    sessionId: string,
    sessionDescription: RealtimeSessionDescription,
  ) {
    return this.request<Record<string, never>>(
      `/api/rooms/${encodeURIComponent(roomId)}/media/${encodeURIComponent(sessionId)}/renegotiate`,
      { method: "PUT", body: JSON.stringify({ sessionDescription }) },
    );
  }
  submitReport(input: {
    targetType: string;
    targetId: string;
    category: string;
    description: string;
  }) {
    return this.request<{ reportId: string; status: string }>("/api/reports", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  submitFeedback(input: {
    kind: "feedback" | "issue" | "support";
    subject: string;
    message: string;
    email?: string;
    phone?: string;
    context?: {
      source: "contact_page" | "dashboard" | "header" | "room";
      path?: string;
      roomId?: string;
      roomCode?: string;
      gameId?: string;
      userAgent?: string;
      platform?: string;
      language?: string;
      languages?: string[];
      browserTimeZone?: string;
      viewportWidth?: number;
      viewportHeight?: number;
      screenWidth?: number;
      screenHeight?: number;
      pixelRatio?: number;
      touchPoints?: number;
      connectionType?: string;
      countryCode?: string | null;
      currencyCode?: string | null;
      city?: string | null;
      region?: string | null;
      localeSource?: string;
    };
    turnstileToken: string;
  }) {
    return this.request<{
      requestId: string;
      status: string;
      acknowledgmentEmail: "accepted" | "failed" | "not_requested";
    }>("/api/feedback", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
}
